import {
  createContext,
  Dispatch,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  HelpDocument,
  HelpDocumentService,
  MccsRoles,
} from '@/@types/generated'
import { Navigate, Outlet } from 'react-router'
import { useError } from '@/hooks/useError'
import { FileUploadRequest } from '@/@types/client/FileUploadRequest'
import { saveAs } from 'file-saver'
import { HasFeature, hasRole } from '@/components/Atoms/Permissions'
import { HelpDocumentsFeature } from '@/lib/features/help-documents'
import { buildPath } from '@/routes'
import { useAuth } from '@/hooks/useAuth'

type HelpDocState = {
  documents: HelpDocument[]
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
  hasUploadAbility: boolean
  uploadFile: (data: FileUploadRequest) => Promise<void>
  downloadDocument: (documentId?: string) => Promise<void>
  removeDocument: (documentId?: string) => Promise<void>
}

const HelpDocContext = createContext<HelpDocState | undefined>(undefined)
export const useHelpDoc = (): HelpDocState => {
  const context = use(HelpDocContext)
  if (context === undefined) {
    throw new Error('useHelpDoc must be used within a HelpDocContext')
  }

  return context
}

const HelpDocumentProvider = () => {
  const { user } = useAuth()
  const { showError, showSuccess } = useError()

  const [isLoading, setIsLoading] = useState(false)
  const [documents, setDocuments] = useState<HelpDocument[]>([])

  const _getHelpDocuments = useCallback(
    async (e?: any) => {
      e?.preventDefault()
      e?.stopPropagation()

      try {
        setIsLoading(true)

        const response = await HelpDocumentService.getApiHelpDocument()

        setDocuments(response)
      } catch (e: any) {
        console.error('Error retrieving help document', e)
        showError('Error retrieving help document')
      } finally {
        setIsLoading(false)
      }
    },
    [showError]
  )

  const _uploadFile = async (request: FileUploadRequest) => {
    try {
      setIsLoading(true)

      await HelpDocumentService.postApiHelpDocument(request as any)

      await _getHelpDocuments()
      showSuccess('Uploaded help document successfully.')
    } catch (error: any) {
      console.error(`ERROR...${error.message}`)
      showError(`Upload failed: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const _downloadDocument = async (documentId?: string) => {
    try {
      setIsLoading(true)

      if (!documentId) return

      const document = await HelpDocumentService.getApiHelpDocument1(documentId)

      saveAs(document.url ?? '', document.label ?? '')
    } catch (e) {
      console.error('Error downloading help document: ', e)
      showError('Error downloading help document')
    } finally {
      setIsLoading(false)
    }
  }

  const _removeDocument = async (documentId?: string) => {
    try {
      setIsLoading(true)

      if (!documentId) return

      await HelpDocumentService.deleteApiHelpDocument(documentId)
      await _getHelpDocuments()

      showSuccess('Document deleted')
    } catch (e) {
      console.error('Error removing help document: ', e)
      showError('Error removing help document')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    _getHelpDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasUploadAbility = hasRole(
    [
      MccsRoles.ADMINISTRATOR,
      MccsRoles.CHIEF_INVESTIGATOR,
      MccsRoles.CREDENTIALING_ADMIN,
    ],
    user
  )

  return (
    <HasFeature
      feature={HelpDocumentsFeature}
      noFeatureComponent={<Navigate to={buildPath('/')} />}
    >
      <HelpDocContext
        value={{
          isLoading,
          setIsLoading,
          documents,
          hasUploadAbility,
          uploadFile: _uploadFile,
          downloadDocument: _downloadDocument,
          removeDocument: _removeDocument,
        }}
      >
        <Outlet />
      </HelpDocContext>
    </HasFeature>
  )
}

export default HelpDocumentProvider
